import { useContext } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { QueryKey, UseMutationOptions, UseMutationResult } from "@tanstack/react-query";
import { ToastContext, ToastContextType, LoadingContext, LoadingContextType, LangContext, LangContextType } from "./customHooks";
import textData from "../textData.json";

/**
 * Toast message that can carry both languages. Pass a textData leaf directly
 * (its shape IS { eng, fr }) for founder-facing mutations; plain strings stay
 * valid for admin-facing mutations (admins are hard-forced to English).
 */
export type LocalizedMessage = string | { eng: string; fr: string };

const resolveMessage = (
  message: LocalizedMessage | undefined,
  lang: "eng" | "fr"
): string | undefined =>
  message === undefined || typeof message === "string" ? message : message[lang];

/**
 * Wrapper around useMutation that integrates with ToastContext and LoadingContext.
 *
 * Provides: loading overlay, success/error toasts, and cache invalidation —
 * all in one place so components don't need to wire these manually.
 *
 * Component-specific side effects (navigate, close modal) are passed via
 * the per-call `mutate(args, { onSuccess })` override.
 */

interface MutationUIOptions<TData, TVariables> {
  mutationFn: (variables: TVariables) => Promise<TData>;
  /**
   * Query keys to invalidate on success. REQUIRED — pass `[]` (with a comment
   * explaining why) only for mutations that genuinely change no UI-visible
   * state (e.g., resend-email actions). For any mutation that touches data
   * viewable from another role, include `queryKeys.dashboards.all` to cascade
   * invalidation across role dashboards via TanStack's prefix matching. See
   * CLAUDE.md → State Sync Discipline.
   */
  invalidateKeys: QueryKey[];
  /** Toast message shown on success (omit to skip toast) */
  successMessage?: LocalizedMessage;
  /** Toast message shown on error (defaults to generic message) */
  failureMessage?: LocalizedMessage;
  /** Whether to show the full-screen loading overlay (default: true) */
  showLoadingOverlay?: boolean;
  /** Hook-level onSuccess — runs before per-call onSuccess */
  onSuccess?: (data: TData, variables: TVariables) => void;
  /** Hook-level onError */
  onError?: (error: Error, variables: TVariables) => void;
  /** Custom success check for non-standard response shapes (default: checks data.success) */
  checkSuccess?: (data: TData) => boolean;
}

export function useMutationWithUI<TData = unknown, TVariables = void>(
  options: MutationUIOptions<TData, TVariables>
): UseMutationResult<TData, Error, TVariables> {
  const queryClient = useQueryClient();
  const showToast = (useContext(ToastContext) as ToastContextType).showToast;
  const { showLoading, hideLoading } = useContext(LoadingContext) as LoadingContextType;
  // "eng" fallback matters: existing tests mount mutation hooks without LangContextProvider
  const langState = (useContext(LangContext) as LangContextType | null)?.langState ?? "eng";

  const {
    mutationFn,
    invalidateKeys,
    successMessage,
    failureMessage,
    showLoadingOverlay = true,
    onSuccess: hookOnSuccess,
    onError: hookOnError,
    checkSuccess = (data: TData) => (data as any)?.success === true,
  } = options;

  const successMsg = resolveMessage(successMessage, langState);
  const failureMsg = resolveMessage(failureMessage, langState);

  const mutationOptions: UseMutationOptions<TData, Error, TVariables> = {
    mutationFn: async (variables: TVariables) => {
      const result = await mutationFn(variables);
      if (!checkSuccess(result)) {
        throw new Error((result as any)?.message || failureMsg || textData.Toasts.Common.OperationFailed[langState]);
      }
      return result;
    },

    onMutate: () => {
      if (showLoadingOverlay) showLoading();
    },

    onSuccess: (data, variables) => {
      if (successMsg) {
        showToast({ toastType: "success", message: successMsg });
      }
      if (invalidateKeys.length) {
        invalidateKeys.forEach((key) => {
          queryClient.invalidateQueries({ queryKey: key });
        });
      }
      hookOnSuccess?.(data, variables);
    },

    onError: (error, variables) => {
      showToast({
        toastType: "failure",
        message: error.message || failureMsg || textData.Toasts.Common.SomethingWentWrong[langState],
      });
      hookOnError?.(error, variables);
    },

    onSettled: () => {
      if (showLoadingOverlay) hideLoading();
    },
  };

  return useMutation(mutationOptions);
}
